import { sepAPI } from '../API';
import {
  CreateSEPBody,
  GetSEPResponse,
  SEP,
  SEPSearchTask,
  SEPSearchResult,
  SearchParams,
  UpdateSEPBody,
} from '../../../shared/types/SEP';
import { setSnackbarForEndpoint } from '../../utils/snackbar';
import { TaskExtended } from '../../../shared/types/Task';
import { UserShort } from '../../../shared/types/User';

export const sepSlice = sepAPI.injectEndpoints({
  endpoints: (builder) => ({
    getSep: builder.query<GetSEPResponse, number>({
      query: (id) => `sep/${id}`,
      providesTags: ['SEPs'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting this SEP.',
        });
      },
    }),
    getAllSEPIDs: builder.query<number[], void>({
      query: () => `allSEPIDs`,
      providesTags: ['SEPs'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting this SEP IDs.',
        });
      },
    }),
    getMultipleSEPs: builder.query<any, { ids: number[] }>({
      query: (arg) => {
        const {
          ids,
        } = arg;
        return {
          url: 'selected-seps',
          params: {
            ids: ids.join(','),
          },
          method: 'GET',
        };
      },
      providesTags: ['SEPs'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting the list of SEPs.',
        });
      },
    }),
    findSeps: builder.query<SEPSearchResult, SearchParams>({
      query: (arg) => {
        const {
          limit,
          offset,
          sortBy,
          sortAsc,
          phase,
          search,
          mine,
          myToDos,
          status,
          workFlowType,
          createdAt,
          validationDate,
          completedAt,
          dataFieldColumns,
          keyTopics,
          filterApplied
        } = arg;
        return {
          url: 'seps',
          params: {
            limit,
            offset,
            sortBy,
            sortAsc,
            phase,
            search,
            mine,
            myToDos,
            status,
            workFlowType,
            createdAt,
            validationDate,
            completedAt,
            dataFieldColumns,
            keyTopics,
            filterApplied
          },
        };
      },
      providesTags: ['SEPs'],
      onQueryStarted: (arg, api) => {
        setSnackbarForEndpoint(api, {
          errorMessage: 'There was a problem getting the list of SEPs.',
        });
      },
    }),
    createSep: builder.mutation<SEP, Partial<CreateSEPBody>>({
      query: ({ ...body }) => {
        return {
          url: `sep`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['SEPs', 'Tasks'],
      onQueryStarted: (arg, api) => {
        const sepName = (arg as any).name || 'SEP';
        setSnackbarForEndpoint(api, {
          successMessage: `SEP has been created successfully!`,
          errorMessage: 'There was a problem creating the SEP.',
        });
      },
    }),
    partialSep: builder.query<SEPSearchResult, string>({
      query: (loggedInUserID) => ({
        url: `seps?creator.id=${loggedInUserID}&phase=knockout&filterApplied=true&mine=true`,
        method: 'GET',
        providesTags: ['SEPs'],
      }),
    }),
    updateSep: builder.mutation<SEP, { id: number; body: UpdateSEPBody }>({
      query: ({ id, body }) => ({
        url: `sep/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['SEPs', 'TasksForSEP', 'Tasks'],
      onQueryStarted(arg, api) {
        setSnackbarForEndpoint(api, {
          successMessage: `SEP details have been updated successfully!`,
          errorMessage: 'There was a problem updating the SEP.',
        }, 12000);
      },
    }),
    updateSepRequestor: builder.mutation<SEP, { id: number; body: UserShort; oldRequestorName?: string }>({
      query: ({ id, body }) => ({
        url: `sep/${id}/requestor`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['SEPs', 'TasksForSEP', 'Tasks'],
      onQueryStarted(arg, api) {
        const requestorName = (arg as any).body?.displayName || (arg as any).body?.givenName || 'Requestor';
        const oldRequestorName = (arg as any).oldRequestorName || 'Previous Requestor';
        const sepID = arg.id;
        setSnackbarForEndpoint(api, {
          successMessage: `Requestor changed from ${oldRequestorName} to ${requestorName} successfully!`,
          errorMessage: 'There was a problem updating the SEP requestor.',
        }, 20000);
      },
    }),
    getSepTasks: builder.query<TaskExtended[], number>({
      query: (id) => `sep/${id}/tasks?includeDisabled=true`,
      providesTags: ['TasksForSEP'],
       onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(sepAPI.util.invalidateTags(['Comments']));
      },
    }),
    updateSepStatus: builder.mutation<
      SEP,
      { sepID: number; status: string; reason: string }
    >({
      query: ({ sepID, status, reason }) => ({
        url: `sep/update-status`,
        method: 'PATCH',
        body: { sepID, status, reason },
      }),
      invalidatesTags: ['SEPs'],
      onQueryStarted(arg, api) {
        // Action-specific messages for SEP status changes
        let successMessage = 'SEP status updated!';
        if (arg && arg.status) {
          switch (arg.status.toLowerCase()) {
            case 'rejected':
              successMessage = `SEP #${arg.sepID} rejected successfully!`;
              break;
            case 'cancelled':
              successMessage = `SEP #${arg.sepID} cancelled successfully!`;
              break;
            default:
              successMessage = `SEP #${arg.sepID} updated successfully!`;
          }
        }
        setSnackbarForEndpoint(api, {
          successMessage,
          errorMessage: 'There was a problem updating the SEP status.',
        });
      },
    }),
    updateKnockoutCancelSepStatus: builder.mutation<
      SEP,
      { sepID: number; status: string }
    >({
      query: ({ sepID, status }) => ({
        url: `sep/update-cancel-status`,
        method: 'PATCH',
        body: { sepID, status },
      }),
      invalidatesTags: ['SEPs'],
      onQueryStarted(arg, api) {
        // Action-specific messages for SEP cancel
        let successMessage = `SEP #${arg.sepID} updated successfully!`;
        if (arg && arg.status && arg.status.toLowerCase() === 'cancelled') {
          successMessage = `SEP #${arg.sepID} has been cancelled`;
        }
        setSnackbarForEndpoint(api, {
          successMessage,
          errorMessage: 'There was a problem updating the SEP.',
        });
      },
    }),
    getSubscribedSeps:builder.query<(GetSEPResponse & { tasks: SEPSearchTask[] })[], string>({
      query: (loggedInUserID) => ({
        url: `subscribed-sep?userEmail=${loggedInUserID}`,
        method: 'GET',
        providesTags: ['SEPs'],
      }),
    }),
    updateSepSubscriptions: builder.mutation<SEP, { id: number; body: UpdateSEPBody }>({
      query: ({ id, body }) => ({
        url: `sep-subscription/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['SEPs', 'TasksForSEP', 'Tasks'],
      onQueryStarted(arg, api) {
        // Action-specific messages for SEP subscription changes
        let successMessage = 'Subscription updated!';
        const action = arg && arg.body && (arg.body as any).action;
        const sepID = arg.id;
        let snackbarDuration = 6000;
        
        if (action) {
          switch (action.toLowerCase()) {
            case 'subscribed':
              successMessage = `You are now subscribed to SEP #${sepID}`;
              break;
            case 'unsubscribed':
              successMessage = `You have unsubscribed from SEP #${sepID}`;
              snackbarDuration = 9000;
              break;
            default:
              successMessage = `SEP #${sepID} updated successfully!`;
          }
        }
        setSnackbarForEndpoint(api, {
          successMessage,
          errorMessage: 'There was a problem updating your SEP subscription.',
        }, snackbarDuration);
      },
    }),
  }),
});

export const {
  useGetSepQuery,
  useFindSepsQuery,
  useCreateSepMutation,
  usePartialSepQuery,
  useUpdateSepMutation,
  useGetSepTasksQuery,
  useUpdateSepRequestorMutation,
  useUpdateSepStatusMutation,
  useUpdateKnockoutCancelSepStatusMutation,
  useGetSubscribedSepsQuery,
  useUpdateSepSubscriptionsMutation,
  useGetAllSEPIDsQuery,
  useGetMultipleSEPsQuery,
} = sepSlice;
